/**
 * PersonalizationBadge - Visual indicator of personalization status
 *
 * Shows whether search results are personalized based on wallet connection.
 * Follows Pentatonic design system.
 */

import React from "react";
import { Sparkles, User, Wallet } from "lucide-react";

import { useWallet } from "../../contexts/WalletContext";

/**
 * Compact badge showing personalization status
 */
export function PersonalizationBadge({
  variant = "default",
  showConnectHint = true,
  className = "",
}) {
  const { isConnected, walletProfile, connectWallet } = useWallet();
  const [isConnecting, setIsConnecting] = React.useState(false);

  const handleConnect = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsConnecting(true);
    try {
      await connectWallet();
    } catch (err) {
      console.error("Error connecting wallet:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  if (isConnected) {
    // Connected state - show personalized badge
    if (variant === "minimal") {
      return (
        <span
          className={`inline-flex items-center gap-1 text-xs text-penta-black ${className}`}
          title={`Personalized for ${walletProfile?.email || "you"}`}
        >
          <Sparkles size={12} className="text-penta-mint" />
          <span className="font-light">Personalized</span>
        </span>
      );
    }

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 bg-penta-mint/10 border border-penta-mint/30 ${className}`}
      >
        <Sparkles size={14} className="text-penta-mint" />
        <span className="text-xs font-light text-penta-black">
          Personalized for you
        </span>
        {walletProfile?.displayName && (
          <span className="text-xs text-penta-grey-medium hidden sm:inline">
            ({walletProfile.displayName})
          </span>
        )}
      </div>
    );
  }

  // Not connected state
  if (!showConnectHint) {
    return null;
  }

  if (variant === "minimal") {
    return (
      <button
        onClick={handleConnect}
        disabled={isConnecting}
        className={`inline-flex items-center gap-1 text-xs text-penta-grey-medium hover:text-penta-black transition-colors duration-200 ${className}`}
        title="Connect wallet for personalized results"
      >
        <User size={12} />
        <span className="font-light">
          {isConnecting ? "Connecting..." : "Generic results"}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting}
      className={`inline-flex items-center gap-2 px-3 py-1.5 bg-penta-grey-light border border-penta-grey-medium hover:border-penta-black transition-all duration-200 ${className}`}
    >
      <Wallet size={14} className="text-penta-grey-medium" />
      <span className="text-xs font-light text-penta-black">
        {isConnecting ? "Connecting..." : "Connect for personalized results"}
      </span>
    </button>
  );
}

/**
 * Status indicator for search results header
 */
export function SearchPersonalizationStatus({ resultCount = 0, className = "" }) {
  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="font-light text-penta-black">
          {resultCount > 0 ? `Found ${resultCount} products` : "Searching..."}
        </span>
      </div>
      <PersonalizationBadge variant="minimal" showConnectHint={true} />
    </div>
  );
}

/**
 * Explanation text for why results are personalized
 */
export function PersonalizationExplanation({ className = "" }) {
  const { isConnected, walletProfile } = useWallet();

  if (!isConnected) {
    return null;
  }

  return (
    <p className={`text-xs text-penta-grey-medium font-light ${className}`}>
      <Sparkles size={10} className="inline text-penta-mint mr-1" />
      Results tailored to your preferences
      {walletProfile?.displayName && ` for ${walletProfile.displayName}`}
    </p>
  );
}

// Default export
export default PersonalizationBadge;
